import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bookmark,
  Pencil,
  Play,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import clsx from 'clsx';
import type { SavedQuery, SessionUser } from '@idas/shared';
import { api, ApiError } from '../lib/api';
import { EmptyState, ListSkeleton } from './ui/EmptyState';
import { InlineSpinner } from './ui/Refreshing';
import { Modal } from './ui/Modal';
import { toast } from './ui/Toast';
import { ShareQueryModal } from './ShareQueryModal';

interface Props {
  user: SessionUser;
  dataSourceId: string | null;
  /** Opens the query in a new editor tab. */
  onOpen: (query: SavedQuery) => void;
  /** Opens it and runs it straight away. */
  onRun: (query: SavedQuery) => void;
}

/**
 * Saved queries.
 *
 * Lives beside the schema tree rather than on its own page: reaching for a
 * saved query is the same motion as reaching for a table, and a query you have
 * to navigate away to find is one people re-type instead.
 */
export function SavedQueriesPanel({ user, dataSourceId, onOpen, onRun }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SavedQuery | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<SavedQuery | null>(null);
  const [sharing, setSharing] = useState<SavedQuery | null>(null);

  const saved = useQuery({ queryKey: ['saved-queries'], queryFn: api.savedQueries.list });

  const remove = useMutation({
    mutationFn: (id: string) => api.savedQueries.remove(id),
    onSuccess: () => {
      toast.success('Saved query deleted.');
      void queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
      setConfirmDelete(null);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not delete it.'),
  });

  const { mine, shared } = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const all = (saved.data?.queries ?? []).filter(
      (q) =>
        !needle ||
        q.name.toLowerCase().includes(needle) ||
        q.sql.toLowerCase().includes(needle) ||
        (q.description ?? '').toLowerCase().includes(needle),
    );
    return {
      mine: all.filter((q) => q.ownerId === user.id),
      // Someone else's, shared with everyone.
      shared: all.filter((q) => q.ownerId !== user.id),
    };
  }, [saved.data, search, user.id]);

  return (
    <div className="flex h-full flex-col bg-surface-raised">
      <div className="border-b border-edge px-2 py-1.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-ink-faint" />
          <input
            className="input py-1 pl-7 pr-7 text-2xs"
            placeholder="Search saved queries"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {saved.isFetching && !saved.isLoading && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2">
              <InlineSpinner />
            </span>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {saved.isLoading && <ListSkeleton rows={4} height={38} />}

        {!saved.isLoading && mine.length === 0 && shared.length === 0 && (
          <EmptyState
            icon={Bookmark}
            title={search ? 'Nothing matches' : 'No saved queries yet'}
            body={
              search
                ? 'Try a different search term.'
                : 'Write a query, then press Save in the toolbar to keep it here — useful for anything you run more than once.'
            }
          />
        )}

        {mine.length > 0 && (
          <Section label="Yours">
            {mine.map((query) => (
              <QueryRow
                key={query.id}
                query={query}
                owned
                stale={dataSourceId !== null && query.dataSourceId !== dataSourceId}
                onOpen={() => onOpen(query)}
                onRun={() => onRun(query)}
                onEdit={() => setEditing(query)}
                onDelete={() => setConfirmDelete(query)}
                onShare={() => setSharing(query)}
              />
            ))}
          </Section>
        )}

        {shared.length > 0 && (
          <Section label="Shared with you">
            {shared.map((query) => (
              <QueryRow
                key={query.id}
                query={query}
                owned={false}
                stale={dataSourceId !== null && query.dataSourceId !== dataSourceId}
                onOpen={() => onOpen(query)}
                onRun={() => onRun(query)}
              />
            ))}
          </Section>
        )}
      </div>

      {editing && (
        <SavedQueryModal
          query={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
            setEditing(null);
          }}
        />
      )}

      {sharing && <ShareQueryModal query={sharing} onClose={() => setSharing(null)} />}

      {confirmDelete && (
        <Modal
          title={`Delete “${confirmDelete.name}”?`}
          width="sm"
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button className="btn" onClick={() => setConfirmDelete(null)}>
                Keep it
              </button>
              <button
                className="btn border-transparent bg-danger text-white hover:bg-danger/90"
                onClick={() => remove.mutate(confirmDelete.id)}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </>
          }
        >
          <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded border border-edge bg-surface-sunken p-2 font-mono text-2xs text-ink-muted">
            {confirmDelete.sql}
          </pre>
          {confirmDelete.shared && (
            <p className="mt-2 text-2xs leading-relaxed text-warn">
              This query is shared — colleagues using it will lose it.
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-1">
      <p className="px-2 py-1 text-2xs font-semibold uppercase tracking-wider text-ink-faint">
        {label}
      </p>
      {children}
    </div>
  );
}

function QueryRow({
  query,
  owned,
  stale,
  onOpen,
  onRun,
  onEdit,
  onDelete,
  onShare,
}: {
  query: SavedQuery;
  owned: boolean;
  /** Saved against a different connection than the one selected. */
  stale: boolean;
  onOpen: () => void;
  onRun: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
}) {
  return (
    <div className="group flex items-center gap-1 px-2 py-1 hover:bg-surface-sunken">
      <button
        className="flex min-w-0 flex-1 flex-col items-start text-left"
        onClick={onOpen}
        title={query.sql}
      >
        <span className="flex w-full items-center gap-1">
          <Bookmark className="h-3 w-3 shrink-0 text-ink-faint" />
          <span className="truncate text-2xs text-ink">{query.name}</span>
          {query.shared && owned && (
            <Users className="h-2.5 w-2.5 shrink-0 text-accent" aria-label="Shared with everyone" />
          )}
          {!query.shared && owned && query.sharedWith.length > 0 && (
            <span
              className="shrink-0 text-2xs text-accent"
              title={`Shared with ${query.sharedWith.length} ${query.sharedWith.length === 1 ? 'person' : 'people'}`}
            >
              <Users className="inline h-2.5 w-2.5" /> {query.sharedWith.length}
            </span>
          )}
          {query.sharedWithMe && (
            <span
              className="chip shrink-0 border-accent/30 bg-accent/10 text-accent"
              title={`${query.ownerName} shared this with you`}
            >
              for you
            </span>
          )}
        </span>
        <span className="truncate pl-4 text-2xs text-ink-faint">
          {owned ? query.description || firstLine(query.sql) : `${query.ownerName} · ${firstLine(query.sql)}`}
        </span>
      </button>

      {/* A query saved against another connection would run against whatever
          is selected now, which may not even have the table. Flagged rather
          than blocked — switching connection is the user's call. */}
      {stale && (
        <span
          className="shrink-0 text-2xs text-warn"
          title="Saved against a different connection"
        >
          ≠
        </span>
      )}

      <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <button
          className="rounded p-0.5 text-ink-faint hover:text-ok"
          onClick={onRun}
          title="Open and run"
          aria-label={`Run ${query.name}`}
        >
          <Play className="h-3 w-3 fill-current" />
        </button>
        {onShare && (
          <button
            className="rounded p-0.5 text-ink-faint hover:text-accent"
            onClick={onShare}
            title="Choose who can see this"
            aria-label={`Share ${query.name}`}
          >
            <Users className="h-3 w-3" />
          </button>
        )}
        {onEdit && (
          <button
            className="rounded p-0.5 text-ink-faint hover:text-accent"
            onClick={onEdit}
            title="Edit"
            aria-label={`Edit ${query.name}`}
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
        {onDelete && (
          <button
            className="rounded p-0.5 text-ink-faint hover:text-danger"
            onClick={onDelete}
            title="Delete"
            aria-label={`Delete ${query.name}`}
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </span>
    </div>
  );
}

function firstLine(sql: string): string {
  const line = sql.split('\n').find((l) => l.trim()) ?? '';
  return line.trim().slice(0, 60);
}

/**
 * Create or edit. One component for both, since the fields are identical and
 * "save as new" is just the create path with the SQL prefilled.
 */
export function SavedQueryModal({
  query,
  draftSql,
  dataSourceId,
  onClose,
  onSaved,
}: {
  /** Present when editing an existing query. */
  query?: SavedQuery;
  /** Present when saving the editor's current contents as a new query. */
  draftSql?: string;
  dataSourceId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(query?.name ?? '');
  const [description, setDescription] = useState(query?.description ?? '');
  const [sql, setSql] = useState(query?.sql ?? draftSql ?? '');
  const [shared, setShared] = useState(query?.shared ?? false);
  const [attempted, setAttempted] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        sql,
        dataSourceId: query?.dataSourceId ?? dataSourceId ?? '',
        shared,
      };
      return query ? api.savedQueries.update(query.id, body) : api.savedQueries.create(body);
    },
    onSuccess: () => {
      toast.success(query ? 'Saved query updated.' : `Saved as “${name.trim()}”.`);
      onSaved();
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not save it.'),
  });

  const nameMissing = name.trim().length === 0;

  return (
    <Modal
      title={query ? `Edit “${query.name}”` : 'Save this query'}
      description="Give it a name you will recognise in a month."
      width="lg"
      onClose={onClose}
      footer={
        <>
          {attempted && nameMissing && (
            <span className="mr-auto text-2xs text-warn">A name is required.</span>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => (nameMissing ? setAttempted(true) : save.mutate())}
          >
            {save.isPending ? 'Saving…' : query ? 'Save changes' : 'Save query'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-2xs font-medium text-ink-muted">
            Name <span className="text-danger">*</span>
          </span>
          <input
            className={clsx('input', attempted && nameMissing && 'border-warn')}
            placeholder="e.g. Active user count"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-2xs font-medium text-ink-muted">
            Description <span className="text-ink-faint">(optional)</span>
          </span>
          <input
            className="input"
            placeholder="What it answers, and any caveats."
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-2xs font-medium text-ink-muted">SQL</span>
          <textarea
            className="input min-h-[160px] resize-y font-mono leading-relaxed"
            value={sql}
            onChange={(event) => setSql(event.target.value)}
            spellCheck={false}
          />
        </label>

        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5 h-3.5 w-3.5 rounded border-edge-strong accent-accent"
            checked={shared}
            onChange={(event) => setShared(event.target.checked)}
          />
          <span>
            <span className="block text-2xs font-medium text-ink">Share with colleagues</span>
            <span className="block text-2xs leading-relaxed text-ink-muted">
              Everyone can see and run it; only you can change it. Their results are still
              filtered by their own permissions — sharing a query never shares data.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
